<template>
  <div class="container v-content-container">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-male"></i>
          <span v-if="selectedCategoryId != null">
            {{
              `${$label("category.category", "Category")}${
                noDataExist ? "" : ": " + originalName
              }`
            }}
          </span>
          <span v-else-if="!loading">
            {{ $label("category.new_category", "New category") }}
          </span>
        </h1>
      </div>
    </div>

    <navigation-tabs></navigation-tabs>

    <div class="row mt-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <div v-show="loading" class="row">
        <div class="col-xs-12">
          <view-loader
            margin-top-pixels="15"
            border-color="primary"
          ></view-loader>
        </div>
      </div>

      <div v-show="!loading" class="row">
        <div class="col-sm-6">
          <h2>
            <i class="fa fa-fw fa-lg fa-male"></i>
            {{ $label("category.category", "Category") }}
          </h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <!-- BEGIN Name-->
                  <section class="col col-12">
                    <text-input
                      v-model="selectedCategory.name"
                      id="category-name"
                      :label="$label('common.name', 'Name')"
                      :placeholder="$label('common.name', 'Name')"
                      :has-error="hasError('name')"
                      :error="getError('name')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateCategory"
                    />
                  </section>
                  <!-- END Name -->
                </div>

                <section>
                  <label class="label">{{
                    $label("common.booking_restriction", "Booking restriction")
                  }}</label>
                  <label class="select">
                    <select name="number_of_bookings">
                      <option value="">{{
                        $label("category.unlimited", "Unlimited")
                      }}</option>
                      <option value="1">{{
                        $label("category.1_booking", "1 booking")
                      }}</option>
                      <option value="2">{{
                        $label("category.2_bookings", "2 bookings")
                      }}</option>
                      <option value="3">{{
                        $label("category.3_bookings", "3 bookings")
                      }}</option>
                      <option value="4">{{
                        $label("category.4_bookings", "4 bookings")
                      }}</option>
                      <option value="5">{{
                        $label("category.5_bookings", "5 bookings")
                      }}</option>
                    </select>
                    <i></i>
                  </label>
                  <label class="note">{{
                    $label(
                      "category.for_one_participant",
                      "For one participant"
                    )
                  }}</label>
                </section>
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12">&nbsp;</div>
      </div>
      <hr />

      <div v-show="!loading" class="row">
        <div class="col-xs-12 text-center">
          <back-button
            back-link="EventCategories"
            :class="{ 'is-disabled': updating }"
            id="back-category-button"
            icon="fa fa-lg fa-fw fa-arrow-circle-o-left"
            :text="$label('common.back', 'Back')"
          >
          </back-button>
          &nbsp;
          <info-button
            :class="{ 'is-disabled': updating }"
            id="save-category-button"
            icon="fa fa-lg fa-fw fa-save"
            :text="$label('common.save', 'Save')"
            @click.native="createOrUpdateCategory"
          >
          </info-button>
          &nbsp;
          <danger-button
            :class="{ 'is-disabled': updating || selectedCategoryId == null }"
            id="delete-category-button"
            icon="fa fa-fw fa-lg fa-trash-o"
            :text="$label('common.delete', 'Delete')"
            @click.native="deleteCategory"
          >
          </danger-button>
        </div>
      </div>

      <div class="row mb-4">
        <div class="col-xs-12"></div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/api.js";
import swal from "sweetalert";
import FormErrorsMixin from "@/mixins/FormErrors";

import ViewLoader from "@/components/Common/ViewLoader";
import TextInput from "@/components/Common/Inputs/TextInput.vue";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import BackButton from "@/components/Common/Buttons/BackButton";
import DangerButton from "@/components/Common/Buttons/DangerButton";
import NavigationTabs from "@/components/Settings/Events/NavigationTabs.vue";
import Error404 from "@/components/Common/Error404";

export default {
  name: "EventCategoryDetails.vue",

  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    TextInput,
    InfoButton,
    BackButton,
    DangerButton,
    NavigationTabs,
    Error404
  },

  data() {
    return {
      loading: false,
      updating: false,
      noDataExist: false,
      originalName: null,
      selectedCategory: {
        name: null
      },
      selectedCategoryId: null,
      failedRequest: false,
      validatedInputs: ["name"]
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedCategoryId = vm.$route.params.category_id;
      if (
        typeof vm.selectedCategoryId !== "undefined" &&
        vm.selectedCategoryId !== null
      ) {
        if (vm.$route.query.category_created) {
          swal("Success!", "The category was created.", "success").then(() => {
            window.history.pushState(
              {},
              document.title,
              window.location.pathname
            );
          });
        }
        vm.getCategory(() => {
          //
        });
      }
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (
      typeof this.selectedCategoryId !== "undefined" &&
      this.selectedCategoryId !== null
    ) {
      this.getCategory(() => {
        //
      });
    }
    next();
  },

  mounted() {
    //
  },

  methods: {
    createOrUpdateCategory() {
      if (this.selectedCategoryId == null) {
        this.createCategory();
      } else {
        this.updateCategory();
      }
    },

    createCategory() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("categories");

      Api()
        .post(endpoint, this.selectedCategory)
        .then(response => {
          this.selectedCategoryId = response.data.data.id;
          this.$router.push({
            name: "EventCategoryDetails",
            params: { category_id: this.selectedCategoryId },
            query: { category_created: true }
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#category-name", 500);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    getCategory() {
      if (this.loading) {
        return;
      }

      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint(
        "categories/" + this.selectedCategoryId
      );

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedCategory = response.data.data;
          this.originalName = this.selectedCategory.name;
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateCategory() {
      if (this.loading || this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "categories/" + this.selectedCategoryId
      );

      Api()
        .put(endpoint, this.selectedCategory)
        .then(() => {
          this.getCategory(() => {
            //
          });
          swal({
            title: "Success!",
            text: "The category was updated.",
            icon: "success"
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#category-name", 500);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    deleteCategory() {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The category will be deleted",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "categories/" + this.selectedCategoryId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router.push({
                name: "EventCategories",
                query: { category_deleted: true }
              });
            })
            .catch(error => {
              console.log(error.response);
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete categories.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    }
  }
};
</script>

<style scoped>
.mb-4 {
  margin-bottom: 1.2rem;
}
</style>
